import React, { useEffect, useState, useCallback } from "react";
import { dashboardAPI } from "../../services/api/dashboard";
import { timeAgo } from "./utils";
import Pagination, { paginate } from "./Pagination";

const METRICS = [
  { key: "latency_ms", label: "Latency", unit: "ms", defaultThreshold: 2000 },
  { key: "silence_detection", label: "Silence", unit: "", defaultThreshold: 2 },
  { key: "stop_time_after_interruption", label: "Stop time", unit: "ms", defaultThreshold: 1500 },
];

export default function NeedsAttention({ clientId, days, agentId, failedCalls = [], navigate }) {
  const [metricKey, setMetricKey] = useState("latency_ms");
  const [threshold, setThreshold] = useState(2000);
  const [breaches, setBreaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  const metric = METRICS.find((m) => m.key === metricKey) || METRICS[0];

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await dashboardAPI.getMetricBreaches({ clientId, metricKey, threshold: Number(threshold), days, agentId });
      setBreaches(res.data?.breaches || []);
    } catch (_) {
      setBreaches([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, metricKey, threshold, days, agentId]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset to the first page whenever the filters that change the row set change.
  useEffect(() => {
    setPage(0);
  }, [metricKey, threshold, days, agentId]);

  const rows = [
    ...breaches.map((b) => ({
      ...b,
      kind: "breach",
      reason: `High ${metric.label.toLowerCase()} ${Math.round(b.value)}${b.unit || metric.unit}`,
    })),
    ...failedCalls.map((c) => ({ ...c, kind: "failed", reason: "Failed" })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const { slice, safePage, pageCount } = paginate(rows, page);

  return (
    <div className="bg-card rounded-xl border border-border p-5 h-full flex flex-col">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-foreground">Needs attention</h3>
        <div className="flex items-center gap-2">
          <select
            aria-label="metric"
            value={metricKey}
            onChange={(e) => {
              const m = METRICS.find((x) => x.key === e.target.value);
              setMetricKey(e.target.value);
              if (m) setThreshold(m.defaultThreshold);
            }}
            className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:ring-primary/20 focus:border-primary"
          >
            {METRICS.map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground">&gt;</span>
          <input
            aria-label="threshold"
            type="number"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            className="w-20 text-xs border border-gray-300 rounded-lg px-2 py-1 focus:ring-primary/20 focus:border-primary"
          />
          <span className="text-xs text-muted-foreground">{metric.unit}</span>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2 flex-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-10 rounded bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center flex-1">
          No issues in the last {days} days 🎉
        </p>
      ) : (
        <ul className="divide-y divide-border flex-1">
          {slice.map((r) => (
            <li key={`${r.kind}-${r.id}`}>
              <button onClick={() => navigate(`/observability/conversations/${r.id}`)}
                className="w-full flex items-center justify-between py-2 text-left hover:bg-gray-50 rounded px-1">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{r.agent_name || "—"}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    Call #{r.id}{r.call_duration != null ? ` · ${r.call_duration}s` : ""} · {timeAgo(r.created_at)}
                  </p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                  r.kind === "breach" ? "bg-amber-50 text-amber-600" : "bg-rose-50 text-rose-600"
                }`}>
                  {r.reason}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Pagination
        page={safePage}
        pageCount={pageCount}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />
    </div>
  );
}
